/**
 * Supabase Auth client (web2 migration, step 1). Used ONLY for authentication — the
 * browser never reads or writes game tables (RLS is service_role only); every game call
 * still goes through our backend with the session JWT that POST /auth/supabase issues.
 *
 * Returns null when VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are unset, so a build
 * without them degrades to wallet-only sign-in instead of crashing at module load.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase: SupabaseClient | null =
  url && anonKey
    ? createClient(url, anonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          // Completes magic-link and OAuth redirects that land back on the app.
          detectSessionInUrl: true,
          flowType: 'pkce',
        },
      })
    : null;

export const isSupabaseAuthEnabled = supabase !== null;

/** The signed-in Supabase user's current access token, or null when there is no Supabase session. */
export async function getSupabaseAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
